import { WorkflowViewModel } from '@ail/workflows-api'
import { HttpTransportType, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr'
import { USER_MANAGER } from '../../auth/UserManager'
import { Environment } from '../../environment'

interface WorkflowEvents {
  receiveWorkflowCreated: WorkflowViewModel
  receiveWorkflowUpdated: WorkflowViewModel
  receiveWorkflowDeleted: string
}

const WorkflowEventNames: Record<WorkflowEventKey, string> = {
  receiveWorkflowCreated: 'ReceiveWorkflowCreated',
  receiveWorkflowUpdated: 'ReceiveWorkflowUpdated',
  receiveWorkflowDeleted: 'ReceiveWorkflowDeleted',
}

type WorkflowEventKey = keyof WorkflowEvents

type WorkflowHub = {
  [K in WorkflowEventKey]: (handler: (message: WorkflowEvents[K]) => void) => WorkflowHub
} & {
  start: (cancel: Promise<void>) => void
}

const WorkflowEventKeys = Object.keys(WorkflowEventNames) as WorkflowEventKey[]

const connection = new HubConnectionBuilder()
  .withUrl(Environment.WorkflowsHubUrl, {
    accessTokenFactory: async () => {
      const user = await USER_MANAGER.getUser()
      return user?.access_token ?? ''
    },
    transport: HttpTransportType.WebSockets,
    skipNegotiation: true,
  })
  .withAutomaticReconnect()
  .build()

export function workflowHub(): WorkflowHub {
  const hub = {} as WorkflowHub
  const handlers: { eventKey: WorkflowEventKey; handler: never }[] = []
  for (const eventKey of WorkflowEventKeys) {
    hub[eventKey] = (handler) => {
      handlers.push({ eventKey, handler: handler as never })
      return hub
    }
  }

  hub.start = async (cancel: Promise<void>) => {
    try {
      if (connection.state === HubConnectionState.Disconnected) {
        await connection.start()
      }
      for (const handler of handlers) {
        connection.on(handler.eventKey, handler.handler)
      }
      await cancel
    } finally {
      for (const handler of handlers) {
        connection.off(handler.eventKey, handler.handler)
      }
    }
  }

  return hub
}
